import React, { useEffect, useRef, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api, { uploadQrMenuImage, getFileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";

const DEFAULT_CATEGORY_ORDER = [
  "salata",
  "başlangıç",
  "baslangic",
  "corba",
  "çorba",
  "ana yemek",
  "anayemek",
  "yemek",
  "balık",
  "balik",
  "makarna",
  "tatlı",
  "tatli",
  "icecek",
  "içecek",
];

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replaceAll("ı", "i")
    .replaceAll("ğ", "g")
    .replaceAll("ü", "u")
    .replaceAll("ş", "s")
    .replaceAll("ö", "o")
    .replaceAll("ç", "c");

const sortCategoriesByDefaultFlow = (list = []) =>
  [...list].sort((a, b) => {
    const aName = normalizeText(a.name);
    const bName = normalizeText(b.name);

    const aIndex = DEFAULT_CATEGORY_ORDER.findIndex((key) =>
      aName.includes(normalizeText(key))
    );

    const bIndex = DEFAULT_CATEGORY_ORDER.findIndex((key) =>
      bName.includes(normalizeText(key))
    );

    const safeA = aIndex === -1 ? 999 : aIndex;
    const safeB = bIndex === -1 ? 999 : bIndex;

    if (safeA !== safeB) return safeA - safeB;

    return Number(a.index ?? 0) - Number(b.index ?? 0);
  });

const getDiscountedRate = (item = {}) =>
  item.discountedRate ??
  item.DiscountedRate ??
  item.discounted_rate ??
  item.discountRate ??
  item.discount_rate ??
  "";

const parsePrice = (value) => {
  if (value === null || value === undefined || value === "") return 0;

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
};

const calculateDiscountedPrice = (price, discountedRate) => {
  const priceNumber = Number(price || 0);
  const rateNumber = Number(discountedRate || 0);

  if (priceNumber <= 0 || rateNumber <= 0) return null;

  return priceNumber - priceNumber * (rateNumber / 100);
};

const formatPrice = (value) => {
  const numberValue = Number(value || 0);

  return numberValue.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const PriceText = ({ item }) => {
  const discountedRate = getDiscountedRate(item);
  const discountedPrice = calculateDiscountedPrice(item.price, discountedRate);

  if (discountedPrice) {
    return (
      <div className="inline-flex max-w-full items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-3 py-2">
        <span className="whitespace-nowrap text-xs font-semibold text-zinc-400 line-through">
          {formatPrice(item.price)} ₺
        </span>

        <span className="whitespace-nowrap text-base font-black tracking-tight text-emerald-600 sm:text-lg">
          {formatPrice(discountedPrice)} ₺
        </span>
      </div>
    );
  }

  return (
    <div className="inline-flex max-w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-3 py-2">
      <span className="whitespace-nowrap text-base font-black tracking-tight text-zinc-950 sm:text-lg">
        {formatPrice(item.price)} ₺
      </span>
    </div>
  );
};

export default function MenuManagement() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);

  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const [categoryForm, setCategoryForm] = useState({ name: "" });

  const [itemForm, setItemForm] = useState({
    name: "",
    description: "",
    price: "",
    discountedRate: "",
    image_url: "",
    imageId: null,
    is_active: true,
  });

  const [imageUploading, setImageUploading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const startLongPress = (callback) => {
    longPressTriggeredRef.current = false;

    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }

    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      callback();
    }, 350);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const getBusinessSubdomain = () =>
    currentUser?.businessSubDomain ||
    currentUser?.BusinessSubDomain ||
    currentUser?.subDomain ||
    currentUser?.SubDomain ||
    currentUser?.business_sub_domain ||
    currentUser?.businessSubdomain ||
    "";

  const getBusinessId = () =>
    currentUser?.businessId ??
    currentUser?.BusinessId ??
    currentUser?.business?.id ??
    currentUser?.business?.Id ??
    0;

  useEffect(() => {
    const rawUser = localStorage.getItem("user");

    if (!rawUser) {
      toast.error("Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.");
      return;
    }

    try {
      setCurrentUser(JSON.parse(rawUser));
    } catch (err) {
      console.error("Kullanıcı bilgisi parse edilemedi:", err);
      toast.error("Kullanıcı bilgisi okunamadı. Lütfen tekrar giriş yapın.");
    }
  }, []);

  useEffect(() => {
    if (currentUser) fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (selectedCategory) fetchMenuItems(selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const subdomain = getBusinessSubdomain();

      if (!subdomain) {
        toast.error("İşletme subdomain bilgisi bulunamadı.");
        return;
      }

      const response = await api.getCategories({
        businessSubDomain: subdomain,
        page: 0,
        pageSize: 1000,
        visible: true,
      });

      const sortedList = sortCategoriesByDefaultFlow(response.data || []);

      setCategories(sortedList);

      if (sortedList.length > 0) {
        setSelectedCategory((prev) => prev ?? sortedList[0].id);
      } else {
        setSelectedCategory(null);
        setMenuItems([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Kategoriler alınırken bir hata oluştu.");
    }
  };

  const fetchMenuItems = async (categoryId) => {
    if (!categoryId) return;

    try {
      const response = await api.getProducts({
        page: 0,
        pageSize: 1000,
        categoryId,
      });

      setMenuItems(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("İçerikler alınırken bir hata oluştu.");
    }
  };

  const handleCreateCategory = async () => {
    try {
      const businessId = getBusinessId();

      if (!businessId) {
        toast.error("İşletme bilgisi bulunamadı. Lütfen tekrar giriş yapın.");
        return;
      }

      if (!categoryForm.name.trim()) {
        toast.error("Kategori adı boş olamaz.");
        return;
      }

      await api.createCategory({
        name: categoryForm.name.trim(),
        index: categories.length,
        businessId,
        visible: true,
      });

      toast.success("Kategori oluşturuldu.");
      setShowCategoryDialog(false);
      setCategoryForm({ name: "" });
      fetchCategories();
    } catch (error) {
      console.error(error);
      toast.error("Kategori oluşturulurken bir hata oluştu.");
    }
  };

  const handleUpdateCategory = async () => {
    try {
      if (!editingCategory) return;

      if (!categoryForm.name.trim()) {
        toast.error("Kategori adı boş olamaz.");
        return;
      }

      await api.updateCategory({
        id: editingCategory.id,
        name: categoryForm.name.trim(),
        index: editingCategory.index ?? 0,
        slug: editingCategory.slug || "",
      });

      toast.success("Kategori güncellendi.");
      setShowCategoryDialog(false);
      setCategoryForm({ name: "" });
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error(error);
      toast.error("Kategori güncellenirken bir hata oluştu.");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (
      !window.confirm(
        "Bu kategoriyi ve içindeki tüm içerikleri silmek istiyor musunuz?"
      )
    ) {
      return;
    }

    try {
      await api.deleteCategory(id);
      toast.success("Kategori silindi.");

      if (selectedCategory === id) {
        setSelectedCategory(null);
      }

      fetchCategories();
      setMenuItems([]);

      if (editingCategory?.id === id) {
        setShowCategoryDialog(false);
        setEditingCategory(null);
        setCategoryForm({ name: "" });
      }
    } catch (error) {
      console.error(error);
      toast.error("Kategori silinirken bir hata oluştu.");
    }
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name });
    setShowCategoryDialog(true);
  };

  const resetItemForm = () => {
    setItemForm({
      name: "",
      description: "",
      price: "",
      discountedRate: "",
      image_url: "",
      imageId: null,
      is_active: true,
    });
    setImageUploading(false);
  };

  const validateItemForm = () => {
    if (!itemForm.name.trim()) {
      toast.error("İçerik adı boş olamaz.");
      return false;
    }

    const price = parsePrice(itemForm.price);
    const discountedRate =
      itemForm.discountedRate === "" ? null : Number(itemForm.discountedRate);

    if (price <= 0) {
      toast.error("Fiyat 0'dan büyük olmalı.");
      return false;
    }

    if (
      discountedRate !== null &&
      (Number.isNaN(discountedRate) ||
        discountedRate < 0 ||
        discountedRate >= 100)
    ) {
      toast.error("İndirim yüzdesi 0 ile 100 arasında olmalı.");
      return false;
    }

    return true;
  };

  const createItemPayload = () => {
    const discountedRate =
      itemForm.discountedRate === "" ? 0 : Number(itemForm.discountedRate);

    return {
      name: itemForm.name.trim(),
      description: itemForm.description?.trim() || "",
      price: parsePrice(itemForm.price),
      discountedRate,
      calories: 0,
      categoryIds: [],
      promotionIds: [],
      ingredients: "",
      imageId: itemForm.imageId ? Number(itemForm.imageId) : null,
      status: itemForm.is_active,
    };
  };

  const handleCreateItem = async () => {
    if (!selectedCategory) {
      toast.error("Lütfen önce bir kategori seçin.");
      return;
    }

    if (!validateItemForm()) return;

    try {
      const payload = createItemPayload();
      const res = await api.createMenuItem(payload);
      const createdProductId = res?.data?.id;

      if (createdProductId) {
        await api.addProductsToCategory({
          id: selectedCategory,
          productIds: [createdProductId],
        });
      }

      toast.success("İçerik eklendi.");
      setShowItemDialog(false);
      resetItemForm();
      fetchMenuItems(selectedCategory);
    } catch (error) {
      console.error(error);
      toast.error("İçerik eklenirken bir hata oluştu.");
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    if (!validateItemForm()) return;

    try {
      const payload = createItemPayload();

      await api.updateMenuItem(editingItem.id, payload);

      const oldCategoryIds = getItemCategoryIds(editingItem);

      if (oldCategoryIds.length > 0) {
        await api.deleteProductsFromCategory({
          id: Number(oldCategoryIds[0]),
          productIds: [editingItem.id],
        });
      }

      await api.addProductsToCategory({
        id: selectedCategory,
        productIds: [editingItem.id],
      });

      toast.success("İçerik güncellendi.");
      setShowItemDialog(false);
      resetItemForm();
      setEditingItem(null);
      fetchMenuItems(selectedCategory);
    } catch (error) {
      console.error(error);
      toast.error("İçerik güncellenirken bir hata oluştu.");
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Bu menü öğesini silmek istiyor musunuz?")) return;

    try {
      if (selectedCategory) {
        await api.deleteProductsFromCategory({
          id: selectedCategory,
          productIds: [id],
        });
      }

      await api.deleteMenuItem(id);

      toast.success("İçerik silindi.");
      fetchMenuItems(selectedCategory);

      if (editingItem?.id === id) {
        setShowItemDialog(false);
        setEditingItem(null);
        resetItemForm();
      }
    } catch (error) {
      console.error(error);
      toast.error("İçerik silinirken bir hata oluştu.");
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const currentStatus = item.status ?? item.is_active ?? true;

      await api.changeProductStatus(item.id, !currentStatus);

      fetchMenuItems(selectedCategory);
    } catch (error) {
      console.error(error);
      toast.error("İçerik durumu güncellenirken bir hata oluştu.");
    }
  };

  const openEditItem = (item) => {
    const fileUrl =
      item?.imageFile?.fileUrl || item?.imageFile?.details?.[0]?.fileUrl || "";

    const resolvedImageUrl =
      item.image_url || (fileUrl ? getFileUrl(fileUrl) : "");

    setEditingItem(item);
    setItemForm({
      name: item.name || "",
      description: item.description || "",
      price: item.price?.toString() ?? "",
      discountedRate: getDiscountedRate(item)?.toString() ?? "",
      image_url: resolvedImageUrl,
      imageId: item.imageId ?? null,
      is_active: item.status ?? item.is_active ?? true,
    });
    setShowItemDialog(true);
  };

  const handleImageFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImageUploading(true);

      const uploaded = await uploadQrMenuImage(file);

      if (!uploaded?.id) {
        toast.error("Görsel yükleme başarılı ama imageId alınamadı.");
        return;
      }

      setItemForm((prev) => ({
        ...prev,
        imageId: uploaded.id,
        image_url: uploaded.url,
      }));

      toast.success("Görsel yüklendi.");
    } catch (error) {
      console.error("UPLOAD ERROR FULL:", error);
      console.error("STATUS:", error?.response?.status);
      console.error("DATA:", error?.response?.data);

      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.title ??
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null) ??
        error?.message ??
        "Bilinmeyen hata";

      toast.error(`Görsel yükleme hatası: ${msg}`);
    } finally {
      setImageUploading(false);
      event.target.value = "";
    }
  };

  const getItemCategoryIds = (item = {}) => {
    if (Array.isArray(item.categoryIds)) return item.categoryIds.map(String);

    if (Array.isArray(item.categories)) {
      return item.categories
        .map((category) =>
          typeof category === "object"
            ? category.id ?? category.categoryId
            : category
        )
        .filter(Boolean)
        .map(String);
    }

    const singleCategoryId =
      item.categoryId ??
      item.CategoryId ??
      item.category_id ??
      item.menuCategoryId ??
      null;

    return singleCategoryId ? [String(singleCategoryId)] : [];
  };

  const filteredItems = selectedCategory
    ? menuItems.filter((item) =>
        getItemCategoryIds(item).includes(String(selectedCategory))
      )
    : [];

  return (
    <AdminLayout>
      {/* =====================================================
          MOBILE — WebMenuMobile MenuManagementScreen parity
      ====================================================== */}
      <div
        className="min-h-[calc(100vh-3.5rem)] bg-[#F8F8F8] px-5 pt-5 md:hidden"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 40px)",
        }}
        data-testid="menu-management-mobile"
      >
        <section className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="mb-1 text-[11px] font-bold tracking-[1px] text-[#EA580C]">
                MENÜ DÜZENİ
              </div>

              <h1 className="text-[22px] font-bold leading-7 text-[#18181B]">
                Kategoriler
              </h1>
            </div>

            <button
              type="button"
              aria-label="Yeni kategori ekle"
              onClick={() => {
                setEditingCategory(null);
                setCategoryForm({ name: "" });
                setShowCategoryDialog(true);
              }}
              className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#FFF7ED] text-[25px] font-normal leading-7 text-[#EA580C] transition active:opacity-70"
            >
              ＋
            </button>
          </div>

          <div className="-mx-5 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max gap-2 pr-4">
              {categories.map((category) => {
                const active = selectedCategory === category.id;

                return (
                  <button
                    key={category.id}
                    type="button"
                    onPointerDown={() =>
                      startLongPress(() => openEditCategory(category))
                    }
                    onPointerUp={cancelLongPress}
                    onPointerCancel={cancelLongPress}
                    onPointerLeave={cancelLongPress}
                    onContextMenu={(event) => event.preventDefault()}
                    onClick={() => {
                      if (longPressTriggeredRef.current) {
                        longPressTriggeredRef.current = false;
                        return;
                      }

                      setSelectedCategory(category.id);
                    }}
                    className={`flex min-h-10 items-center justify-center rounded-[14px] border px-4 text-sm font-semibold transition active:opacity-60 ${
                      active
                        ? "border-[#EA580C] bg-[#FFF7ED] text-[#EA580C]"
                        : "border-[#E4E4E7] bg-white text-[#52525B]"
                    }`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="mt-3 text-xs leading-4 text-[#A1A1AA]">
            Düzenlemek için kategoriye basılı tutun
          </p>
        </section>

        <section>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-[22px] font-bold leading-7 text-[#18181B]">
                {selectedCategory !== null
                  ? categories.find((item) => item.id === selectedCategory)
                      ?.name || "İçerikler"
                  : "İçerikler"}
              </h2>

              <p className="mt-1 text-[13px] leading-5 text-[#71717A]">
                {selectedCategory !== null
                  ? `${filteredItems.length} içerik · düzenlemek için basılı tutun`
                  : "Önce bir kategori seçin"}
              </p>
            </div>

            <button
              type="button"
              disabled={selectedCategory === null}
              onClick={() => {
                if (!selectedCategory) return;
                setEditingItem(null);
                resetItemForm();
                setShowItemDialog(true);
              }}
              className="flex min-h-10 shrink-0 items-center justify-center rounded-[14px] bg-[#EA580C] px-3 text-sm font-semibold text-white transition active:opacity-70 disabled:opacity-40"
            >
              ＋ İçerik
            </button>
          </div>

          {selectedCategory === null ? (
            <div className="rounded-2xl border border-[#E4E4E7] bg-white px-6 py-8 text-center">
              <div className="text-base font-bold text-[#18181B]">
                Bir kategori seçin
              </div>

              <p className="mt-2 text-[13px] leading-5 text-[#71717A]">
                İçerikleri görüntülemek için yukarıdaki kategorilerden
                birini seçin.
              </p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="rounded-2xl border border-[#E4E4E7] bg-white px-6 py-8 text-center">
              <div className="text-base font-bold text-[#18181B]">
                Bu kategori henüz boş
              </div>

              <p className="mt-2 text-[13px] leading-5 text-[#71717A]">
                İlk içeriğinizi ekleyerek kategoriyi oluşturmaya başlayın.
              </p>

              <button
                type="button"
                onClick={() => {
                  setEditingItem(null);
                  resetItemForm();
                  setShowItemDialog(true);
                }}
                className="mt-5 min-h-[42px] rounded-[14px] bg-[#EA580C] px-4 text-sm font-semibold text-white transition active:opacity-70"
              >
                İlk İçeriği Ekle
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item, index) => (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  aria-label={item.name}
                  onPointerDown={() =>
                    startLongPress(() => openEditItem(item))
                  }
                  onPointerUp={cancelLongPress}
                  onPointerCancel={cancelLongPress}
                  onPointerLeave={cancelLongPress}
                  onContextMenu={(event) => event.preventDefault()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openEditItem(item);
                    }
                  }}
                  className="flex min-h-[104px] select-none rounded-2xl border border-[#E4E4E7] bg-white px-4 py-3 shadow-[0_6px_18px_rgba(24,24,27,0.04)] transition active:bg-[#F4F4F5]"
                >
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      draggable="false"
                      className="mr-3 h-16 w-16 shrink-0 rounded-[14px] bg-[#F4F4F5] object-cover"
                    />
                  ) : (
                    <div className="mr-3 flex h-16 w-16 shrink-0 items-center justify-center rounded-[14px] bg-[#FFF7ED] text-[22px] font-bold text-[#EA580C]">
                      {item.name?.charAt(0)?.toUpperCase() || "•"}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex min-h-[30px] items-center gap-2">
                      <div className="min-w-0 flex-1 truncate text-base font-bold leading-[21px] text-[#18181B]">
                        {item.name}
                      </div>

                      <div
                        className="-mr-1 shrink-0 scale-[0.82]"
                        onPointerDown={(event) => event.stopPropagation()}
                        onPointerUp={(event) => event.stopPropagation()}
                      >
                        <Switch
                          checked={item.status ?? item.is_active ?? true}
                          onCheckedChange={() => handleToggleActive(item)}
                          data-testid={`toggle-active-mobile-${item.id}`}
                        />
                      </div>
                    </div>

                    {item.description ? (
                      <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-[#71717A]">
                        {item.description}
                      </p>
                    ) : (
                      <p className="mt-1 text-[13px] leading-5 text-[#A1A1AA]">
                        Açıklama eklenmemiş
                      </p>
                    )}

                    <div className="mt-2 text-base font-bold leading-[21px] text-[#18181B]">
                      {formatPrice(item.price)} ₺
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* DESKTOP — Premium Web Menü içerik yönetimi */}
      <div
        className="hidden md:block"
        data-testid="menu-management-page"
      >
        <div className="mx-auto w-full max-w-[1180px] space-y-6">
          <section className="relative overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_24px_70px_rgba(24,24,27,0.05)] lg:p-8">
            <div
              aria-hidden="true"
              className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-orange-100/80 blur-3xl"
            />

            <div className="relative flex items-end justify-between gap-8">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.12em] text-orange-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                  Dijital Vitrin İçeriği
                </div>

                <h1
                  className="mt-5 text-[34px] font-extrabold tracking-[-0.04em] text-zinc-950 lg:text-[42px]"
                  data-testid="menu-heading"
                >
                  İçerik Yönetimi
                </h1>

                <p className="mt-3 max-w-2xl text-sm font-medium leading-6 text-zinc-500">
                  Kategorilerinizi ve müşterilerinizin göreceği içerikleri
                  tek ekrandan düzenleyin.
                </p>
              </div>

              <div className="hidden shrink-0 gap-3 lg:flex">
                <div className="min-w-[118px] rounded-[18px] border border-zinc-200 bg-[#FAFAF9] px-4 py-3">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-zinc-400">
                    Kategori
                  </p>
                  <p className="mt-1 text-2xl font-extrabold tracking-[-0.03em] text-zinc-950">
                    {categories.length}
                  </p>
                </div>

                <div className="min-w-[118px] rounded-[18px] border border-zinc-200 bg-[#FAFAF9] px-4 py-3">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-zinc-400">
                    İçerik
                  </p>
                  <p className="mt-1 text-2xl font-extrabold tracking-[-0.03em] text-zinc-950">
                    {menuItems.length}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="grid min-h-[620px] gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
            <aside
              className="rounded-[28px] border border-zinc-200/80 bg-white p-5 shadow-[0_18px_55px_rgba(24,24,27,0.04)]"
              data-testid="categories-section"
            >
              <div className="flex items-start justify-between gap-4 border-b border-zinc-100 pb-5">
                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                    Kategoriler
                  </p>
                  <h2 className="mt-2 text-lg font-extrabold tracking-[-0.02em] text-zinc-950">
                    Vitrin düzeni
                  </h2>
                  <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                    İçeriklerinizi gruplandırın ve kolayca yönetin.
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={() => {
                    setEditingCategory(null);
                    setCategoryForm({ name: "" });
                    setShowCategoryDialog(true);
                  }}
                  className="h-10 w-10 shrink-0 rounded-full bg-[#EA580C] p-0 text-white shadow-sm hover:bg-[#C2410C]"
                  data-testid="add-category-button"
                  aria-label="Yeni kategori ekle"
                >
                  <Plus className="h-[18px] w-[18px]" />
                </Button>
              </div>

              <div className="mt-5 space-y-2">
                {categories.length === 0 ? (
                  <div className="rounded-[18px] border border-dashed border-zinc-200 bg-[#FAFAF9] px-4 py-8 text-center">
                    <p className="text-sm font-extrabold text-zinc-800">
                      Henüz kategori yok
                    </p>
                    <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                      İlk kategorinizi ekleyerek vitrininizi oluşturmaya başlayın.
                    </p>
                  </div>
                ) : (
                  categories.map((category) => {
                    const active = selectedCategory === category.id;

                    return (
                      <div
                        key={category.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedCategory(category.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedCategory(category.id);
                          }
                        }}
                        className={`group flex cursor-pointer items-center gap-3 rounded-[16px] border px-3 py-3 transition ${
                          active
                            ? "border-orange-200 bg-orange-50"
                            : "border-transparent bg-[#FAFAF9] hover:border-zinc-200 hover:bg-white"
                        }`}
                      >
                        <div
                          className={`grid h-9 w-9 shrink-0 place-items-center rounded-[12px] text-xs font-extrabold ${
                            active
                              ? "bg-[#EA580C] text-white"
                              : "bg-white text-zinc-500 ring-1 ring-zinc-100"
                          }`}
                        >
                          {category.name?.charAt(0)?.toUpperCase() || "•"}
                        </div>

                        <span
                          className={`min-w-0 flex-1 truncate text-sm font-bold ${
                            active ? "text-[#C2410C]" : "text-zinc-700"
                          }`}
                        >
                          {category.name}
                        </span>

                        <div className="flex shrink-0 items-center gap-1 opacity-60 transition group-hover:opacity-100">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 rounded-full p-0 text-zinc-500 hover:bg-white hover:text-zinc-950"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditCategory(category);
                            }}
                            data-testid={`edit-category-${category.id}`}
                            aria-label={`${category.name} kategorisini düzenle`}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>

                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 rounded-full p-0 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCategory(category.id);
                            }}
                            data-testid={`delete-category-${category.id}`}
                            aria-label={`${category.name} kategorisini sil`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </aside>

            <div
              className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]"
              data-testid="menu-items-section"
            >
              <div className="flex items-start justify-between gap-5 border-b border-zinc-100 pb-5">
                <div className="min-w-0">
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-zinc-400">
                    Seçili Kategori
                  </p>
                  <h2 className="mt-2 truncate text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                    {selectedCategory
                      ? categories.find((c) => c.id === selectedCategory)?.name || "İçerikler"
                      : "Bir kategori seçin"}
                  </h2>
                  <p className="mt-1 text-xs font-medium text-zinc-500">
                    {selectedCategory
                      ? `${filteredItems.length} içerik listeleniyor`
                      : "İçerikleri görüntülemek için soldan bir kategori seçin."}
                  </p>
                </div>

                <Button
                  type="button"
                  disabled={!selectedCategory}
                  onClick={() => {
                    setEditingItem(null);
                    resetItemForm();
                    setShowItemDialog(true);
                  }}
                  className="shrink-0 rounded-full bg-[#EA580C] px-5 font-bold text-white shadow-[0_10px_26px_rgba(234,88,12,0.18)] hover:bg-[#C2410C] disabled:opacity-40"
                  data-testid="add-menu-item-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Yeni İçerik
                </Button>
              </div>

              {!selectedCategory ? (
                <div className="flex min-h-[430px] items-center justify-center">
                  <div className="max-w-sm text-center">
                    <div className="mx-auto grid h-14 w-14 place-items-center rounded-[18px] bg-orange-50 text-2xl font-black text-[#EA580C]">
                      01
                    </div>
                    <h3 className="mt-5 text-lg font-extrabold text-zinc-950">
                      Önce bir kategori seçin
                    </h3>
                    <p className="mt-2 text-sm font-medium leading-6 text-zinc-500">
                      Seçtiğiniz kategoriye ait içerikler burada görüntülenecek.
                    </p>
                  </div>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="flex min-h-[430px] items-center justify-center">
                  <div className="max-w-sm text-center">
                    <div className="mx-auto grid h-14 w-14 place-items-center rounded-[18px] bg-orange-50 text-[#EA580C]">
                      <Plus className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 text-lg font-extrabold text-zinc-950">
                      Bu kategori henüz boş
                    </h3>
                    <p className="mt-2 text-sm font-medium leading-6 text-zinc-500">
                      İlk içeriğinizi ekleyerek dijital vitrininizi oluşturmaya başlayın.
                    </p>
                    <Button
                      type="button"
                      onClick={() => {
                        setEditingItem(null);
                        resetItemForm();
                        setShowItemDialog(true);
                      }}
                      className="mt-5 rounded-full bg-zinc-950 px-5 font-bold text-white hover:bg-zinc-800"
                    >
                      İlk İçeriği Ekle
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-5 grid gap-4 xl:grid-cols-2">
                  {filteredItems.map((item) => (
                    <article
                      key={item.id}
                      className="group overflow-hidden rounded-[22px] border border-zinc-200/80 bg-[#FAFAF9] transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-white hover:shadow-[0_16px_36px_rgba(24,24,27,0.07)]"
                      data-testid={`menu-item-${item.id}`}
                    >
                      <div className="flex gap-4 p-4">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="h-24 w-24 shrink-0 rounded-[18px] bg-zinc-100 object-cover"
                          />
                        ) : (
                          <div className="grid h-24 w-24 shrink-0 place-items-center rounded-[18px] bg-orange-50 text-2xl font-black text-[#EA580C]">
                            {item.name?.charAt(0)?.toUpperCase() || "•"}
                          </div>
                        )}

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="line-clamp-2 text-base font-extrabold leading-6 text-zinc-950">
                                {item.name}
                              </h3>
                              <p className="mt-1 line-clamp-2 min-h-10 text-xs font-medium leading-5 text-zinc-500">
                                {item.description || "Açıklama eklenmemiş"}
                              </p>
                            </div>

                            <div className="shrink-0 pt-0.5">
                              <Switch
                                checked={item.status ?? item.is_active ?? true}
                                onCheckedChange={() => handleToggleActive(item)}
                                data-testid={`toggle-active-${item.id}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-3 border-t border-zinc-200/70 bg-white px-4 py-3">
                        <PriceText item={item} />

                        <div className="flex shrink-0 items-center gap-1 rounded-full border border-zinc-200 bg-[#FAFAF9] p-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-9 w-9 rounded-full p-0 text-zinc-500 hover:bg-white hover:text-zinc-950"
                            onClick={() => openEditItem(item)}
                            data-testid={`edit-item-${item.id}`}
                            aria-label={`${item.name} içeriğini düzenle`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>

                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-9 w-9 rounded-full p-0 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                            onClick={() => handleDeleteItem(item.id)}
                            data-testid={`delete-item-${item.id}`}
                            aria-label={`${item.name} içeriğini sil`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent
          className="!bottom-0 !left-0 !right-0 !top-auto !w-full !max-w-none !translate-x-0 !translate-y-0 rounded-b-none rounded-t-[28px] border-[#E4E4E7] bg-white px-5 pb-[calc(env(safe-area-inset-bottom,0px)+20px)] pt-3 shadow-2xl md:!bottom-auto md:!left-1/2 md:!right-auto md:!top-1/2 md:!w-full md:!max-w-lg md:!-translate-x-1/2 md:!-translate-y-1/2 md:rounded-2xl md:p-6 [&>button]:right-5 [&>button]:top-5 [&>button]:h-9 [&>button]:w-9 [&>button]:rounded-full [&>button]:bg-[#F4F4F5] [&>button]:p-2"
          data-testid="category-dialog"
        >
          <div className="mx-auto mb-5 h-[5px] w-[38px] rounded-full bg-[#E4E4E7] md:hidden" />

          <DialogHeader className="mb-5 text-left md:mb-0">
            <DialogTitle className="text-2xl font-bold leading-[30px] text-[#18181B] md:text-lg">
              {editingCategory ? "Kategori Düzenle" : "Yeni Kategori"}
            </DialogTitle>

            <p className="mt-2 text-[13px] leading-5 text-[#71717A] md:hidden">
              Kategorinin vitrinde görünen adını belirleyin.
            </p>
          </DialogHeader>

          <div className="space-y-4 py-0 md:py-4">
            <div className="space-y-2">
              <Label
                htmlFor="category-name"
                className="text-sm font-semibold text-[#52525B]"
              >
                Kategori adı
              </Label>

              <Input
                id="category-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ name: e.target.value })}
                placeholder="Örneğin: Ana Yemekler"
                data-testid="category-name-input"
                className="min-h-[52px] rounded-[14px] border-[#E4E4E7] bg-[#F4F4F5] px-4 text-[15px]"
              />
            </div>
          </div>

          {editingCategory ? (
            <button
              type="button"
              onClick={() => handleDeleteCategory(editingCategory.id)}
              className="mb-5 mt-4 min-h-12 w-full rounded-[14px] bg-[#FEF2F2] text-sm font-semibold text-[#DC2626] transition active:opacity-60 md:hidden"
            >
              Kategoriyi Sil
            </button>
          ) : null}

          <DialogFooter className="mt-5 !grid grid-cols-2 gap-3 sm:space-x-0 md:!flex md:justify-end">
            <Button
              variant="outline"
              onClick={() => setShowCategoryDialog(false)}
              className="min-h-[52px] rounded-[14px] border-[#E4E4E7] bg-white md:min-h-9"
            >
              Vazgeç
            </Button>

            <Button
              onClick={
                editingCategory ? handleUpdateCategory : handleCreateCategory
              }
              data-testid="save-category-button"
              className="min-h-[52px] rounded-[14px] bg-[#EA580C] text-white hover:bg-[#EA580C]/90 md:min-h-9 md:bg-zinc-900 md:hover:bg-zinc-800"
            >
              {editingCategory ? "Güncelle" : "Kategori Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent
          className="!bottom-0 !left-0 !right-0 !top-auto !w-full !max-w-none !translate-x-0 !translate-y-0 overflow-hidden rounded-b-none rounded-t-[28px] border-[#E4E4E7] bg-white p-0 shadow-2xl md:!bottom-auto md:!left-1/2 md:!right-auto md:!top-1/2 md:!w-[94vw] md:!max-w-2xl md:!-translate-x-1/2 md:!-translate-y-1/2 md:rounded-2xl [&>button]:right-5 [&>button]:top-5 [&>button]:h-9 [&>button]:w-9 [&>button]:rounded-full [&>button]:bg-[#F4F4F5] [&>button]:p-2"
          data-testid="menu-item-dialog"
        >
          <div className="max-h-[88vh] overflow-y-auto px-5 pb-5 pt-3 md:max-h-[82vh] md:px-6 md:py-5">
            <div className="mx-auto mb-5 h-[5px] w-[38px] rounded-full bg-[#E4E4E7] md:hidden" />

            <DialogHeader className="mb-5 text-left md:mb-0">
              <DialogTitle className="text-2xl font-bold leading-[30px] text-[#18181B] md:text-lg">
                {editingItem ? "İçerik Düzenle" : "Yeni İçerik"}
              </DialogTitle>

              <p className="mt-2 text-[13px] leading-5 text-[#71717A] md:hidden">
                Müşterilerin vitrinde göreceği içerik bilgilerini düzenleyin.
              </p>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="item-name">İçerik Adı</Label>
                <Input
                  id="item-name"
                  value={itemForm.name}
                  onChange={(e) =>
                    setItemForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Örneğin: Adana Kebap"
                  data-testid="item-name-input"
                  className="min-h-[52px] rounded-[14px] border-[#E4E4E7] bg-[#F4F4F5] px-4 text-[15px] md:min-h-9 md:bg-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="item-description">Açıklama</Label>
                <Textarea
                  id="item-description"
                  value={itemForm.description}
                  onChange={(e) =>
                    setItemForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="İçeriği kısaca açıklayın..."
                  data-testid="item-description-input"
                  className="min-h-[98px] rounded-[14px] border-[#E4E4E7] bg-[#F4F4F5] px-4 py-3 text-[15px] md:bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="item-price">Fiyat</Label>
                  <Input
                    id="item-price"
                    type="number"
                    step="0.01"
                    value={itemForm.price}
                    onChange={(e) =>
                      setItemForm((prev) => ({
                        ...prev,
                        price: e.target.value,
                      }))
                    }
                    placeholder="100"
                    data-testid="item-price-input"
                    className="min-h-[52px] rounded-[14px] border-[#E4E4E7] bg-[#F4F4F5] px-4 text-[15px] md:min-h-9 md:bg-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="item-discounted-rate">İndirim (%)</Label>
                  <Input
                    id="item-discounted-rate"
                    type="number"
                    min="0"
                    max="99"
                    step="1"
                    value={itemForm.discountedRate}
                    onChange={(e) =>
                      setItemForm((prev) => ({
                        ...prev,
                        discountedRate: e.target.value,
                      }))
                    }
                    placeholder="20"
                    data-testid="item-discounted-rate-input"
                    className="min-h-[52px] rounded-[14px] border-[#E4E4E7] bg-[#F4F4F5] px-4 text-[15px] md:min-h-9 md:bg-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="item-image-file"
                  className="text-sm font-semibold text-[#52525B]"
                >
                  İçerik görseli
                </Label>

                <input
                  id="item-image-file"
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  disabled={imageUploading}
                  className="hidden md:block md:w-full md:max-w-full md:text-sm md:file:mr-3 md:file:rounded-lg md:file:border-0 md:file:bg-zinc-900 md:file:px-3 md:file:py-2 md:file:text-sm md:file:font-semibold md:file:text-white"
                />

                <label
                  htmlFor="item-image-file"
                  className={`block overflow-hidden rounded-2xl border border-[#E4E4E7] bg-[#F4F4F5] md:hidden ${
                    imageUploading
                      ? "pointer-events-none opacity-40"
                      : "cursor-pointer active:opacity-75"
                  }`}
                >
                  {itemForm.image_url ? (
                    <img
                      src={itemForm.image_url}
                      alt="İçerik önizleme"
                      className="h-40 w-full bg-[#F4F4F5] object-cover"
                    />
                  ) : (
                    <div className="flex h-[150px] flex-col items-center justify-center px-6 text-center">
                      <div className="text-[30px] font-normal text-[#EA580C]">
                        ＋
                      </div>

                      <div className="mt-2 text-base font-bold text-[#18181B]">
                        Fotoğraf ekle
                      </div>

                      <div className="mt-1 text-[13px] leading-5 text-[#71717A]">
                        Galerinizden içerik görselini seçin
                      </div>
                    </div>
                  )}

                  <div className="flex min-h-[66px] items-center bg-white px-4 py-3">
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="text-sm font-semibold text-[#18181B]">
                        {imageUploading
                          ? "Yükleniyor..."
                          : itemForm.imageId
                          ? "İçerik görseli"
                          : "Görsel seçilmedi"}
                      </div>

                      <div className="mt-1 text-xs text-[#71717A]">
                        {imageUploading
                          ? "Fotoğraf yükleniyor"
                          : itemForm.imageId
                          ? "Değiştirmek için dokunun"
                          : "Seçmek için dokunun"}
                      </div>
                    </div>

                    <div className="text-sm font-semibold text-[#EA580C]">
                      {imageUploading
                        ? "..."
                        : itemForm.imageId
                        ? "Değiştir"
                        : "Seç"}
                    </div>
                  </div>
                </label>

                <div className="hidden rounded-xl border border-zinc-200 bg-zinc-50 p-3 md:block">
                  <div className="text-xs">
                    {imageUploading && (
                      <span className="font-medium text-zinc-500">
                        Görsel yükleniyor...
                      </span>
                    )}

                    {!imageUploading && itemForm.imageId && (
                      <span className="font-semibold text-emerald-600">
                        Görsel yüklendi
                      </span>
                    )}

                    {!imageUploading && !itemForm.imageId && (
                      <span className="text-zinc-400">Görsel seçilmedi</span>
                    )}
                  </div>

                  {itemForm.image_url && (
                    <div className="mt-3 overflow-hidden rounded-xl border border-zinc-200 bg-white">
                      <img
                        src={itemForm.image_url}
                        alt="İçerik önizleme"
                        className="h-32 w-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex min-h-[72px] items-center gap-4 border-y border-[#E4E4E7] md:min-h-0 md:border-0">
                <div className="min-w-0 flex-1 md:hidden">
                  <div className="text-sm font-semibold text-[#18181B]">
                    Vitrinde göster
                  </div>

                  <div className="mt-1 text-xs text-[#71717A]">
                    Pasif içerikler müşterilere görünmez.
                  </div>
                </div>

                <Switch
                  checked={itemForm.is_active}
                  onCheckedChange={(checked) =>
                    setItemForm((prev) => ({ ...prev, is_active: checked }))
                  }
                  data-testid="item-active-toggle"
                />

                <Label className="hidden md:block">
                  Aktif (müşterilere gösterilsin)
                </Label>
              </div>

              {editingItem ? (
                <button
                  type="button"
                  onClick={() => handleDeleteItem(editingItem.id)}
                  className="mt-5 min-h-12 w-full rounded-[14px] bg-[#FEF2F2] text-sm font-semibold text-[#DC2626] transition active:opacity-60 md:hidden"
                >
                  İçeriği Sil
                </button>
              ) : null}
            </div>
          </div>

          <DialogFooter className="mt-5 !grid grid-cols-2 gap-3 border-0 bg-white px-5 pb-[calc(env(safe-area-inset-bottom,0px)+20px)] pt-0 sm:space-x-0 md:!flex md:justify-end md:border-t md:px-6 md:py-4">
            <Button
              variant="outline"
              onClick={() => setShowItemDialog(false)}
              className="min-h-[52px] rounded-[14px] border-[#E4E4E7] bg-white md:min-h-9"
            >
              Vazgeç
            </Button>

            <Button
              onClick={editingItem ? handleUpdateItem : handleCreateItem}
              data-testid="save-item-button"
              className="min-h-[52px] rounded-[14px] bg-[#EA580C] text-white hover:bg-[#EA580C]/90 md:min-h-9 md:bg-zinc-900 md:hover:bg-zinc-800"
            >
              {editingItem ? "Güncelle" : "İçerik Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}